import { useState, useRef } from 'react'
import GoldButton from '../../Ui/GoldButton'
import Icon, { ICONS } from '../../Ui/Icon'
import Modal from '../../Admin/common/Modal'
import {
  useGetProductsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
} from '../../store/api/services'

const EMPTY_FORM = { name: '', price: '', description: '' }

const inputStyle = {
  width: '100%', background: 'var(--bg-input)',
  border: '1px solid var(--border-input)', borderRadius: 8,
  padding: '9px 14px', color: 'var(--text-primary)',
  fontSize: 13, fontFamily: "'Barlow',sans-serif",
  outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
}

const labelStyle = {
  fontSize: 10, color: 'var(--gold-label)', textTransform: 'uppercase',
  letterSpacing: '0.14em', fontFamily: "'Barlow Condensed',sans-serif", marginBottom: 6,
}

// ── Product Form Modal ────────────────────────────────────────────────────────
function ProductModal({ initial, onClose, onSubmit, loading }) {
  const [form, setForm] = useState(
    initial
      ? { name: initial.name, price: initial.price, description: initial.description || '' }
      : EMPTY_FORM
  )
  const [imageFile, setImageFile] = useState(null)
  const [preview, setPreview] = useState(initial?.image || null)
  const fileRef = useRef()

  const handleInput = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }))

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    setImageFile(file)
    setPreview(URL.createObjectURL(file))
  }

  const handleSubmit = () => {
    const fd = new FormData()
    fd.append('name', form.name)
    fd.append('price', form.price)
    fd.append('description', form.description)
    if (imageFile) fd.append('image', imageFile)
    onSubmit(fd)
  }

  const isEdit = !!initial

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'var(--overlay)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '72px 24px 24px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-active)',
          borderRadius: 14,
          width: '100%',
          maxWidth: 760,
          height: 'auto',
          maxHeight: 'calc(100vh - 96px)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
          animation: 'fadeSlide 0.25s ease forwards',
          overflow: 'hidden',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 28px',
          borderBottom: '1px solid var(--border-soft)',
          flexShrink: 0,
        }}>
          <div style={{
            fontFamily: "'Bebas Neue',sans-serif",
            fontSize: 22,
            color: 'var(--gold)',
            letterSpacing: '0.1em',
          }}>
            {isEdit ? 'Edit Product' : 'Add Product'}
          </div>
          <button
            onClick={onClose}
            className="icon-btn"
            style={{
              width: 30,
              height: 30,
              borderRadius: 7,
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: 18,
            }}
          >
            ✕
          </button>
        </div>

        {/* ── Body — scrollable ── */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px 28px',
          display: 'flex',
          gap: 28,
          minHeight: 0,
        }}>
          {/* Left — Image upload */}
          <div style={{
            width: 260,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}>
            <div style={labelStyle}>Product Image</div>
            <div
              onClick={() => fileRef.current.click()}
              style={{
                height: 260,
                borderRadius: 10,
                overflow: 'hidden',
                border: '2px dashed var(--border-input)',
                cursor: 'pointer',
                background: 'var(--bg-input)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 8,
                transition: 'border-color 0.2s',
                position: 'relative',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--gold)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-input)'}
            >
              {preview ? (
                <>
                  <img
                    src={preview}
                    alt="preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
                  />
                  <div style={{
                    position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = 0}
                  >
                    <div style={{ fontSize: 24 }}>📷</div>
                    <div style={{ fontSize: 12, color: '#fff', marginTop: 6 }}>Change image</div>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 36, color: 'var(--text-muted)' }}>📷</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '0 12px' }}>
                    Click to upload<br />
                    <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>JPG, PNG, WEBP</span>
                  </div>
                </>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />

            {preview && (
              <button
                onClick={() => { setPreview(null); setImageFile(null) }}
                style={{
                  background: 'rgba(248,113,113,0.1)',
                  border: '1px solid rgba(248,113,113,0.25)',
                  color: '#F87171',
                  borderRadius: 7,
                  padding: '7px 0',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontFamily: "'Barlow',sans-serif",
                }}
              >
                Remove Image
              </button>
            )}
          </div>

          {/* Right — Fields */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
          }}>
            {[
              { label: 'Product Name', name: 'name', type: 'text', placeholder: 'e.g. Apple iPhone 15 Pro', required: true },
              { label: 'Price (₹)', name: 'price', type: 'number', placeholder: '0.00', required: true },
              { label: 'Description', name: 'description', type: 'text', placeholder: 'Brief description', required: false },
            ].map(({ label, name, type, placeholder, required }) => (
              <div key={name}>
                <div style={labelStyle}>{label}</div>
                {name === 'description' ? (
                  <textarea
                    name={name}
                    placeholder={placeholder}
                    value={form[name]}
                    onChange={handleInput}
                    rows={4}
                    style={{
                      ...inputStyle,
                      resize: 'vertical',
                      minHeight: 100,
                      padding: '10px 14px',
                      lineHeight: 1.6,
                      fontFamily: "'Barlow',sans-serif",
                    }}
                    onFocus={e => e.target.style.borderColor = 'var(--gold)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border-input)'}
                  />
                ) : (
                  <input
                    name={name}
                    type={type}
                    step="0.01"
                    placeholder={placeholder}
                    value={form[name]}
                    onChange={handleInput}
                    required={required}
                    style={inputStyle}
                    onFocus={e => e.target.style.borderColor = 'var(--gold)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border-input)'}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={{
          display: 'flex',
          gap: 10,
          padding: '16px 28px',
          borderTop: '1px solid var(--border-soft)',
          flexShrink: 0,
          background: 'var(--bg-card)',
        }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '10px 0',
              borderRadius: 8,
              border: '1px solid var(--border-main)',
              background: 'transparent',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: 13,
              fontFamily: "'Barlow',sans-serif",
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !form.name || !form.price}
            style={{
              flex: 1,
              padding: '10px 0',
              borderRadius: 8,
              border: 'none',
              background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))',
              color: '#0A0C0F',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 700,
              fontFamily: "'Bebas Neue',sans-serif",
              letterSpacing: '0.08em',
              opacity: loading || !form.name || !form.price ? 0.5 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            {loading ? 'Saving...' : isEdit ? 'Update Product' : 'Create Product'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Product Card ──────────────────────────────────────────────────────────────
function ProductCard({ product, onEdit, onDelete }) {
  return (
    <div className="stat-card" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>

      <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 6, zIndex: 2 }}>
        <button
          onClick={() => onEdit(product)} title="Edit"
          style={{
            width: 30, height: 30, borderRadius: 7,
            border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'var(--gold)', fontSize: 14, transition: 'all 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(232,197,71,0.2)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
        >✎</button>
        <button
          onClick={() => onDelete(product)} title="Delete"
          style={{
            width: 30, height: 30, borderRadius: 7,
            border: '1px solid rgba(248,113,113,0.3)',
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#F87171', fontSize: 14, transition: 'all 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(248,113,113,0.2)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
        >🗑</button>
      </div>

      <div style={{ width: '100%', height: 180, overflow: 'hidden', background: 'rgba(255,255,255,0.03)' }}>
        {product.image ? (
          <img
            src={product.image} alt={product.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s ease' }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: 'var(--text-faint)', fontSize: 36,
          }}>📦</div>
        )}
      </div>

      <div style={{ padding: '14px 16px' }}>
        <div style={{
          fontSize: 14, fontWeight: 600, color: 'var(--text-primary)',
          fontFamily: "'Barlow',sans-serif", marginBottom: 6,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{product.name}</div>

        {product.description && (
          <div style={{
            fontSize: 12, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{product.description}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{
            fontSize: 20, fontFamily: "'Bebas Neue',sans-serif",
            color: 'var(--gold)', letterSpacing: '0.06em',
          }}>₹{parseFloat(product.price).toLocaleString('en-IN')}</span>
          <span style={{
            fontSize: 10, color: 'var(--text-muted)',
            fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '0.08em',
          }}>{product.created_by_email?.split('@')[0]}</span>
        </div>

        <div style={{ marginTop: 12, height: 3, borderRadius: 3, background: 'rgba(255,255,255,0.06)' }}>
          <div style={{
            height: '100%', borderRadius: 3, background: 'var(--gold)',
            width: `${Math.min(100, (parseFloat(product.price) / 2000) * 100)}%`,
            transition: 'width 1s ease',
          }} />
        </div>
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Products() {
  const [search, setSearch] = useState('')
  const [formModal, setFormModal] = useState(null)   // null | 'create' | product object
  const [deleteTarget, setDeleteTarget] = useState(null)

  const { data: products = [], isLoading, isError } = useGetProductsQuery()
  const [createProduct, { isLoading: creating }] = useCreateProductMutation()
  const [updateProduct, { isLoading: updating }] = useUpdateProductMutation()
  const [deleteProduct, { isLoading: deleting }] = useDeleteProductMutation()

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  )

  const avgPrice = products.length
    ? Math.round(products.reduce((a, p) => a + parseFloat(p.price), 0) / products.length)
    : 0

  const handleFormSubmit = async (formData) => {
    try {
      if (formModal === 'create') await createProduct(formData).unwrap()
      else await updateProduct({ id: formModal.id, formData }).unwrap()
      setFormModal(null)
    } catch (err) {
      console.error('Failed to save product:', err)
    }
  }

  const handleDelete = async () => {
    try {
      await deleteProduct(deleteTarget.id).unwrap()
      setDeleteTarget(null)
    } catch (err) {
      console.error('Failed to delete product:', err)
    }
  }

  return (
    <div>

      {/* ── Product Form Modal ── */}
      {formModal && (
        <ProductModal
          initial={formModal === 'create' ? null : formModal}
          onClose={() => setFormModal(null)}
          onSubmit={handleFormSubmit}
          loading={creating || updating}
        />
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteTarget && (
        <Modal
          title="Delete Product"
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          confirmText="Delete"
          loading={deleting}
          danger
        >
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, fontFamily: "'Barlow',sans-serif", margin: 0 }}>
            Are you sure you want to delete&nbsp;
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{deleteTarget.name}</span>?
            <br />
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, display: 'block' }}>
              This action cannot be undone.
            </span>
          </p>
        </Modal>
      )}

      {/* Page Header */}
      <div className="page-header">
        <div>
          <h2 className="page-title">Products</h2>
          <p className="page-subtitle">{products.length} products available</p>
        </div>
        <GoldButton onClick={() => setFormModal('create')}>
          <Icon d={ICONS.plus} size={14} /> &nbsp;Add Product
        </GoldButton>
      </div>

      {/* Search */}
      <div className="filter-row">
        <div className="search-box" style={{ width: 220 }}>
          <Icon d={ICONS.search} size={14} />
          <input
            placeholder="Search products..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Summary */}
      <div className="summary-grid">
        {[
          { label: 'Total Products', value: products.length, color: 'var(--gold)' },
          { label: 'Avg Price', value: `₹${avgPrice.toLocaleString('en-IN')}`, color: '#60A5FA' },
          { label: 'In Stock', value: products.length, color: '#4ADE80' },
          { label: 'Added By', value: `${[...new Set(products.map(p => p.created_by_email))].length} admins`, color: '#A78BFA' },
        ].map(s => (
          <div key={s.label} className="panel" style={{ padding: '14px 18px' }}>
            <div style={{
              fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.12em',
              textTransform: 'uppercase', fontFamily: "'Barlow Condensed',sans-serif", marginBottom: 6,
            }}>{s.label}</div>
            <div style={{
              fontSize: 20, fontFamily: "'Bebas Neue',sans-serif",
              color: s.color, letterSpacing: '0.06em',
            }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* States */}
      {isLoading && <p style={{ color: 'var(--text-muted)', marginTop: 32 }}>Loading products...</p>}
      {isError && <p style={{ color: '#F87171', marginTop: 32 }}>Failed to load products.</p>}

      {/* Card Grid */}
      {!isLoading && !isError && (
        <div className="card-grid">
          {filtered.map(p => (
            <ProductCard
              key={p.id}
              product={p}
              onEdit={svc => setFormModal(svc)}
              onDelete={setDeleteTarget}
            />
          ))}
          {filtered.length === 0 && (
            <p style={{ color: 'var(--text-muted)' }}>No products found.</p>
          )}
        </div>
      )}
    </div>
  )
}